import { hasPermission } from "@avela/avela-authorization-sdk";
import React, { useCallback, useEffect, useMemo } from "react";
import { useAuthorizationScoped } from "src/components/Providers/AuthorizationProvider";
import { useFormTemplates } from "src/components/Providers/FormTemplateProvider";
import {
  ADMISSION_TABS,
  DropoffFormTab,
  FilterTypes,
  FormTabsTypes,
  getAdmissionTab,
  SearchAndFilterTypes,
  Tab,
  VisibilityOptions,
} from "src/constants";
import { useIsOfferEnabled } from "src/hooks/useIsOfferEnabled";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { isNotNull } from "src/services/predicates";
import * as GQL from "src/types/graphql";
import { z } from "zod";
import { FiltersByTab } from "../formFilters/utils";
import { GET_PREVIOUS_FORM_TEMPLATE } from "../graphql/queries";

export type UseFormsListProps = {
  formTemplateId: uuid | undefined;
  searchParams: URLSearchParams;
  setSearchParamsAndClearSelection: (a: any) => void; // TODO replace any
  setFormListImportData: (l: any[]) => void; // TODO replace any
  searchParamDefaultOverrides?: SearchParamDefaultOverrides;
};

export type FormsListTab = {
  current: Tab;
  currentIndex: number;
  onChange: (tab: Tab) => void;
  visibleTabs: readonly Tab[];
};

// allows the caller to override the defaults specified in the useFormsListTab hook
export type SearchParamDefaultOverrides = {
  [SearchAndFilterTypes.Visibility]?: string;
};

const FormTabParser = z.nativeEnum(FormTabsTypes);

export function useFormsListTab({
  formTemplateId,
  searchParams,
  setSearchParamsAndClearSelection,
  setFormListImportData,
  searchParamDefaultOverrides,
}: UseFormsListProps): FormsListTab {
  // On tab change, verifies if there are filters that should be cleared in the SearchParams
  // e.g. there's some filters which exists on the forms/all tabs and don't exist on the offers/waitlist tab
  const onChange = useCallback(
    (newTab: Tab) => {
      setAllTabs((tabs) => {
        return tabs.map((tab) => {
          if (tab.key === newTab.key) {
            return newTab;
          }

          if (
            tab.key === FormTabsTypes.FormListImports &&
            newTab.key !== FormTabsTypes.FormListImports
          ) {
            // hide form list import when clicking away from it since we don't have a proper way to re-render the form list import
            return { ...tab, isVisible: false };
          }

          return tab;
        });
      });

      searchParams.set(SearchAndFilterTypes.Tab, newTab.type);

      if (newTab.type === FormTabsTypes.DropoffForms) {
        searchParams.set(
          SearchAndFilterTypes.DropoffFormTemplateId,
          newTab.formTemplateId
        );
      } else {
        searchParams.delete(SearchAndFilterTypes.DropoffFormTemplateId);
      }

      // if moving away from Imports tab, then clear data and hide tab
      if (newTab.key !== FormTabsTypes.FormListImports) {
        setFormListImportData([]);

        const formListImportsTab = getAdmissionTab(
          FormTabsTypes.FormListImports
        );

        if (!formListImportsTab) {
          throw new Error("Something went wrong!");
        }
      }

      const filtersToBeClear = Object.keys(FilterTypes).filter(
        (filterKey) =>
          !FiltersByTab[newTab.key]?.includes(
            FilterTypes[filterKey as keyof typeof FilterTypes]
          )
      );

      searchParams.delete(SearchAndFilterTypes.SubStatus);
      searchParams.delete(SearchAndFilterTypes.SubStatusDateFrom);
      searchParams.delete(SearchAndFilterTypes.SubStatusDateTo);
      searchParams.delete(SearchAndFilterTypes.SubStatusDaysAgo);
      searchParams.delete(SearchAndFilterTypes.ExpiresAtDateFrom);
      searchParams.delete(SearchAndFilterTypes.ExpiresAtDateTo);
      searchParams.delete(SearchAndFilterTypes.ExpiresAtNextDays);
      searchParams.delete(SearchAndFilterTypes.ExpiresAtDate);

      filtersToBeClear.forEach((filter) => {
        searchParams.delete(
          SearchAndFilterTypes[filter as keyof typeof SearchAndFilterTypes]
        );
      });

      searchParams.delete(SearchAndFilterTypes.VisibleColumns);

      searchParams.set(SearchAndFilterTypes.Offset, "0");

      if (!searchParams.get(SearchAndFilterTypes.Visibility)) {
        searchParams.set(
          SearchAndFilterTypes.Visibility,
          searchParamDefaultOverrides?.[SearchAndFilterTypes.Visibility] ??
            VisibilityOptions.Visible
        );
      }

      setSearchParamsAndClearSelection(searchParams);
    },
    [
      searchParams,
      setFormListImportData,
      setSearchParamsAndClearSelection,
      searchParamDefaultOverrides,
    ]
  );

  const { remoteData: getDropoffFormsData } = useRemoteDataQuery<
    GQL.GetPreviousFormTemplate,
    GQL.GetPreviousFormTemplateVariables
  >(GET_PREVIOUS_FORM_TEMPLATE, {
    variables: { form_template_id: formTemplateId ?? "" },
    skip: formTemplateId === undefined,
  });

  const [allTabs, setAllTabs] = React.useState<readonly Tab[]>(ADMISSION_TABS);

  const dropoffFormTabs: readonly DropoffFormTab[] = useMemo(
    () =>
      getDropoffFormsData
        .map((data) =>
          data.assigned_form_next_form
            .map(({ previous_form_template }) => previous_form_template)
            .filter(isNotNull)
            .map((formTemplate) => {
              return {
                type: FormTabsTypes.DropoffForms as const,
                key: formTemplate.id,
                formTemplateId: formTemplate.id,
                label: formTemplate.name,
                isVisible: true,
              };
            })
        )
        .withDefault([]),
    [getDropoffFormsData]
  );

  useEffect(() => {
    setAllTabs([...ADMISSION_TABS, ...dropoffFormTabs]);
  }, [dropoffFormTabs]);

  const current = useMemo((): Tab => {
    const rawTab = searchParams.get(SearchAndFilterTypes.Tab);
    const formTabType = FormTabParser.safeParse(rawTab);
    const defaultTab = ADMISSION_TABS[0];

    if (formTabType.success) {
      switch (formTabType.data) {
        case FormTabsTypes.DropoffForms:
          const dropoffFormTemplateId = searchParams.get(
            SearchAndFilterTypes.DropoffFormTemplateId
          );
          return (
            dropoffFormTabs.find(
              (tab) =>
                tab.type === FormTabsTypes.DropoffForms &&
                tab.formTemplateId === dropoffFormTemplateId
            ) ?? defaultTab
          );

        default:
          return (
            allTabs.find((tab) => tab.key === formTabType.data) ?? defaultTab
          );
      }
    }

    return defaultTab;
  }, [dropoffFormTabs, searchParams, allTabs]);

  const organization = useOrganization();
  const { selectedNavFormTemplate } = useFormTemplates();
  const isOfferEnabled = useIsOfferEnabled(
    organization,
    selectedNavFormTemplate
  );

  const authorizationMap = useAuthorizationScoped("permissions");

  const hasOfferReadPermission = useMemo(() => {
    if (!authorizationMap.hasData()) {
      return false;
    }

    return hasPermission({
      action: {
        type: "Perform::Action",
        id: "with_permission",
      },
      resource: {
        type: "Permission",
        id: "offer:read",
      },
      authorizationMap: authorizationMap.data,
    });
  }, [authorizationMap]);

  const visibleTabs: readonly Tab[] = useMemo(() => {
    if (!hasOfferReadPermission) {
      return allTabs.filter(
        (tab) => tab.key === FormTabsTypes.All && tab.isVisible
      );
    }

    return allTabs.filter((tab) => {
      if (
        tab.key === FormTabsTypes.Offers ||
        tab.key === FormTabsTypes.Waitlists
      ) {
        return isOfferEnabled && tab.isVisible;
      }

      return tab.isVisible;
    });
  }, [allTabs, isOfferEnabled, hasOfferReadPermission]);

  const currentIndex: number = useMemo(() => {
    const currentKey: string =
      current.type === FormTabsTypes.DropoffForms
        ? current.formTemplateId
        : current.key;
    return visibleTabs.findIndex((tab) => tab.key === currentKey);
  }, [visibleTabs, current]);

  const getTabByParams = useCallback(() => {
    const searchParamsTab = searchParams.get(SearchAndFilterTypes.Tab);
    const dropoffFormTemplateId = searchParams.get(
      SearchAndFilterTypes.DropoffFormTemplateId
    );

    if (searchParamsTab === FormTabsTypes.DropoffForms) {
      return dropoffFormTabs.find(
        (tab) => tab.formTemplateId === dropoffFormTemplateId
      );
    }

    return allTabs.find((tab) => tab.type === searchParamsTab);
  }, [allTabs, dropoffFormTabs, searchParams]);

  useEffect(() => {
    // If the tab is dropoff forms and there are no dropoff form tabs loaded yet, don't proceed
    if (
      searchParams.get(SearchAndFilterTypes.Tab) ===
        FormTabsTypes.DropoffForms &&
      !getDropoffFormsData.hasData()
    ) {
      return;
    }

    const tab = getTabByParams();
    if (!tab || !tab.isVisible) {
      onChange(ADMISSION_TABS[0]);
    }
  }, [getDropoffFormsData, getTabByParams, onChange, searchParams]);

  return { current, currentIndex, onChange, visibleTabs };
}
